import React, { useState } from 'react';
import axios from 'axios';
import './FarmsManagement.css';
import { API_BASE_URL } from '../config';

// const API_BASE_URL = 'http://localhost:3001';

function FarmsManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/farms/search`, {
        params: { q: searchQuery },
        withCredentials: true
      });

      setFarms(response.data.farms || []);
      if (response.data.farms.length === 0) {
        setError('No farms found matching your search');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error searching farms');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFarm = async (farm) => {
    setSelectedFarm(farm);

    // Fetch detailed farm info
    try {
      const response = await axios.get(`${API_BASE_URL}/farms/${farm.herd_id}`, {
        withCredentials: true
      });

      if (response.data.success) {
        setSelectedFarm(response.data.farm);
      }
    } catch (err) {
      console.error('Error fetching farm details:', err);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFarms([]);
    setSelectedFarm(null);
    setError(null);
  };

  return (
    <div className="farms-management">
      <div className="page-header">
        <h1>Farms Management</h1>
        <p className="subtitle">Search and view farm information from the database</p>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <h2>Search Farms</h2>
        <div className="search-controls">
          <input
            type="text"
            placeholder="Enter herd ID or farm name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button className="btn-search" onClick={handleSearch} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          {(searchQuery || farms.length > 0) && (
            <button className="btn-clear" onClick={handleClearSearch}>
              Clear
            </button>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>

      {/* Search Results */}
      {farms.length > 0 && !selectedFarm && (
        <div className="search-results">
          <h2>Search Results ({farms.length} found)</h2>
          <div className="farms-grid">
            {farms.map((farm) => (
              <div
                key={farm.herd_id}
                className="farm-card"
                onClick={() => handleSelectFarm(farm)}
              >
                <div className="farm-card-header">
                  <h3>{farm.farm_name}</h3>
                  <span className="badge">{farm.country || 'N/A'}</span>
                </div>
                <div className="farm-card-body">
                  <div className="info-row">
                    <span className="label">Herd ID:</span>
                    <span className="value">{farm.herd_id}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Total Cows:</span>
                    <span className="value">{farm.total_cows || 0}</span>
                  </div>
                </div>
                <div className="farm-card-footer">
                  <span className="view-link">View Details →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Farm Details */}
      {selectedFarm && (
        <div className="farm-details">
          <div className="details-header">
            <div>
              <h2>{selectedFarm.farm_name}</h2>
              <p className="herd-id">Herd ID: {selectedFarm.herd_id}</p>
            </div>
            <button className="btn-back" onClick={() => setSelectedFarm(null)}>
              ← Back to Results
            </button>
          </div>

          <div className="details-grid">
            <div className="detail-card">
              <div className="detail-icon">🏡</div>
              <div className="detail-content">
                <div className="detail-label">Farm Name</div>
                <div className="detail-value">{selectedFarm.farm_name}</div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-icon">🌍</div>
              <div className="detail-content">
                <div className="detail-label">Country</div>
                <div className="detail-value">{selectedFarm.country || 'N/A'}</div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-icon">🐄</div>
              <div className="detail-content">
                <div className="detail-label">Total Cows</div>
                <div className="detail-value">{selectedFarm.total_cows || 0}</div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-icon">📅</div>
              <div className="detail-content">
                <div className="detail-label">First Calving</div>
                <div className="detail-value">
                  {selectedFarm.first_calving
                    ? new Date(selectedFarm.first_calving).toLocaleDateString()
                    : 'N/A'}
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-icon">📅</div>
              <div className="detail-content">
                <div className="detail-label">Last Calving</div>
                <div className="detail-value">
                  {selectedFarm.last_calving
                    ? new Date(selectedFarm.last_calving).toLocaleDateString()
                    : 'N/A'}
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-icon">🔢</div>
              <div className="detail-content">
                <div className="detail-label">Herd ID</div>
                <div className="detail-value">{selectedFarm.herd_id}</div>
              </div>
            </div>
          </div>

          <div className="farm-actions">
            <button className="btn-action btn-primary">
              View All Cows
            </button>
            <button className="btn-action btn-secondary">
              View Treatment History
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && farms.length === 0 && !error && !selectedFarm && (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>Search for a Farm</h3>
          <p>Enter a herd ID or farm name above to search the database</p>
        </div>
      )}
    </div>
  );
}

export default FarmsManagement;
